import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PyramidalShareholderFormComponent } from '../pyramidal-shareholder-form/pyramidal-shareholder-form.component';
import { PyramidalShareholderService } from '../../../services/pyramidal-shareholder.service';
import { PyramidalShareholderDetailDto } from '../../../models/api/pyramidalShareholderDetailDto';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { MaterialModule } from '../../../shared/material.module';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { MatDialog } from '@angular/material/dialog';
import { AddPyramidalPyramidDialogComponent } from '../add-pyramidal-pyramid-dialog/add-pyramidal-pyramid-dialog.component';
import { EditPyramidalPyramidDialogComponent } from '../edit-pyramidal-pyramid-dialog/edit-pyramidal-pyramid-dialog.component';
import { DeletePyramidDialogComponent, DeletePyramidDialogResult } from '../../large-shareholders/delete-pyramid-dialog/delete-pyramid-dialog.component';
import { DeletePyramidalShareholderPyramidYearsRequest } from '../../../models/api/deletePyramidalShareholderPyramidYearsRequest';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-pyramidal-shareholder-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PyramidalShareholderFormComponent,
    NgxSpinnerModule,
    MaterialModule
  ],
  templateUrl: './pyramidal-shareholder-edit.component.html',
  styleUrls: ['./pyramidal-shareholder-edit.component.scss']
})
export class PyramidalShareholderEditComponent implements OnInit, OnDestroy {
  @ViewChild('pyramidalShareholderForm') pyramidalShareholderForm!: PyramidalShareholderFormComponent;

  form: FormGroup;
  pyramidalShareholderId: string | null = null;
  pyramidalShareholderData: PyramidalShareholderDetailDto | null = null;
  initialTabIndex = 0;
  pyramidsByYear: { [year: number]: any[] } = {};
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private pyramidalShareholderService: PyramidalShareholderService,
    private router: Router,
    private route: ActivatedRoute,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService,
    private confirmationDialogService: ConfirmationDialogService,
    private dialog: MatDialog
  ) {
    this.form = this.fb.group({
      name: ['', Validators.required],
      category: ['', Validators.required],
      subcategory: [''],
      notes: ['']
    });
  }

  ngOnInit() {
    // Subscribe to route parameter changes to handle navigation between different pyramidal shareholders
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('id');
        this.pyramidalShareholderId = id;

        if (this.pyramidalShareholderId) {
          this.loadPyramidalShareholderData();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadPyramidalShareholderData() {
    if (!this.pyramidalShareholderId) return;

    this.spinnerService.show();
    this.pyramidalShareholderService.getPyramidalShareholderById(this.pyramidalShareholderId).subscribe({
      next: (data) => {
        this.pyramidalShareholderData = data;

        // Store pyramids data
        this.pyramidsByYear = data.pyramid || {};

        // Patch basic fields first (without category and subcategory)
        this.form.patchValue({
          name: data.name,
          notes: data.notes
        }, { emitEvent: false });

        // Patch category to trigger subcategories population
        if (data.categoryId) {
          this.form.patchValue({
            category: { id: data.categoryId, name: data.categoryName }
          }, { emitEvent: false });
        } else {
          this.form.patchValue({
            category: null
          }, { emitEvent: false });
        }

        // Wait for subcategories to be populated, then patch subcategory
        setTimeout(() => {
          if (data.subCategoryId) {
            this.form.patchValue({
              subcategory: { id: data.subCategoryId, name: data.subCategoryName }
            }, { emitEvent: false });
          } else {
            this.form.patchValue({
              subcategory: null
            }, { emitEvent: false });
          }
        }, 100);

        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();
        this.toast.error('Error loading pyramidal shareholder', {
          iconTheme: {
            primary: 'var(--mat-sys-error)',
            secondary: '#FFF',
          }
        });
        console.error('Error loading pyramidal shareholder:', error);
      }
    });
  }

  onSubmit() {
    if (this.form.valid && this.pyramidalShareholderId) {
      this.spinnerService.show();

      const formValue = this.form.value;
      const request = {
        name: formValue.name,
        categoryId: formValue.category?.id || formValue.category,
        subCategoryId: formValue.subcategory?.id || formValue.subcategory || '',
        notes: formValue.notes || ''
      };

      this.pyramidalShareholderService.updatePyramidalShareholder(this.pyramidalShareholderId, request).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toast.success('Pyramidal shareholder updated successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          });
          this.router.navigate(['/pyramidal-shareholders/list']);
        },
        error: (error: any) => {
          this.spinnerService.hide();
          this.toast.error('Error updating pyramidal shareholder', {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
          console.error('Error updating pyramidal shareholder:', error);
        }
      });
    }
  }

  onCancel() {
    this.router.navigate(['/pyramidal-shareholders/list']);
  }

  get isPyramidTabActive(): boolean {
    return this.pyramidalShareholderForm?.isPyramidTabActive() ?? false;
  }

  onAddPyramidItem() {
    if (!this.pyramidalShareholderId) return;

    const dialogRef = this.dialog.open(AddPyramidalPyramidDialogComponent, {
      width: '800px',
      data: {
        pyramidalShareholderId: this.pyramidalShareholderId
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Reload pyramid data after successful addition
        this.loadPyramidalShareholderData();
      }
    });
  }

  onEditPyramidItem(pyramidId: string) {
    if (!this.pyramidalShareholderId) return;

    // Find the pyramid item to edit
    const pyramidItem = this.findPyramidItem(pyramidId);
    if (!pyramidItem) {
      this.toast.error('Pyramid item not found');
      return;
    }

    const dialogRef = this.dialog.open(EditPyramidalPyramidDialogComponent, {
      width: '800px',
      data: {
        pyramidItem: pyramidItem,
        pyramidalShareholderId: this.pyramidalShareholderId,
        pyramidsByYear: this.pyramidsByYear
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        // Reload pyramid data after successful update
        this.loadPyramidalShareholderData();
      }
    });
  }

  onDeletePyramidItem(event: { pyramidId: string, year?: number }) {
    if (!this.pyramidalShareholderId) return;

    // Find the pyramid item to get its name for the confirmation dialog
    const pyramidItem = this.findPyramidItem(event.pyramidId);
    const itemName = pyramidItem ? this.getPyramidItemName(pyramidItem) : 'this pyramid item';

    // Get current year from the pyramid item or use provided year
    const currentYear = event.year || (pyramidItem ? new Date(pyramidItem.fromDate).getFullYear() : Number(Helpers.defaultMeasurementYear()));

    // Find all years where this entity exists
    const allYears = this.findYearsForEntity(pyramidItem);

    // Open delete pyramid dialog with hideDeleteCurrentYear flag
    const dialogRef = this.dialog.open(DeletePyramidDialogComponent, {
      width: '640px',
      data: {
        itemName,
        currentYear,
        allYears,
        hideDeleteCurrentYear: true
      }
    });

    dialogRef.afterClosed().subscribe((result: DeletePyramidDialogResult | null) => {
      if (result && result.selectedYears && result.selectedYears.length > 0) {
        this.spinnerService.show();

        // Delete the selected years
        this.deleteSelectedYears(event.pyramidId, itemName, result.selectedYears);
      }
    });
  }

  private findPyramidItem(pyramidId: string): any {
    for (const year in this.pyramidsByYear) {
      const pyramids = this.pyramidsByYear[year];
      const found = pyramids.find(p => p.id === pyramidId);
      if (found) return found;
    }
    return null;
  }

  private getPyramidItemName(pyramid: any): string {
    return pyramid.pyramidPyramidalShareholderName
      || pyramid.largeShareholderName
      || pyramid.companyName
      || pyramid.opbCompanyName
      || pyramid.directorName
      || 'Unknown';
  }

  private findYearsForEntity(pyramidItem: any): number[] {
    if (!pyramidItem || !this.pyramidsByYear) {
      return [];
    }

    // Helper function to check if two pyramid entries represent the same entity
    const isSameEntity = (p1: any, p2: any): boolean => {
      if (p1.directorId && p2.directorId && p1.directorId === p2.directorId) return true;
      if (p1.pyramidPyramidalShareholderId && p2.pyramidPyramidalShareholderId && p1.pyramidPyramidalShareholderId === p2.pyramidPyramidalShareholderId) return true;
      if (p1.largeShareholderId && p2.largeShareholderId && p1.largeShareholderId === p2.largeShareholderId) return true;
      if (p1.companyId && p2.companyId && p1.companyId === p2.companyId) return true;
      if (p1.opbCompanyId && p2.opbCompanyId && p1.opbCompanyId === p2.opbCompanyId) return true;
      return false;
    };

    // Find all years where this entity exists
    const years: number[] = [];
    Object.keys(this.pyramidsByYear).forEach(yearStr => {
      const year = parseInt(yearStr, 10);
      const yearPyramids = this.pyramidsByYear[year];

      if (yearPyramids && yearPyramids.some(p => isSameEntity(pyramidItem, p))) {
        years.push(year);
      }
    });

    // Sort years in descending order
    return years.sort((a, b) => b - a);
  }

  private deleteSelectedYears(pyramidId: string, itemName: string, selectedYears: number[]) {
    if (!this.pyramidalShareholderId) return;

    const pyramidItem = this.findPyramidItem(pyramidId);
    if (!pyramidItem) {
      this.spinnerService.hide();
      this.toast.error('Pyramid item not found');
      return;
    }

    if (selectedYears.length === 0) {
      this.spinnerService.hide();
      this.toast.error('No years selected for deletion');
      return;
    }

    // Single atomic request: the server re-resolves the correct historical row for each year by the
    // node's entity identity (not a client-supplied row id, which goes stale after the first split),
    // so a multi-year span is deleted correctly instead of racing one request per year.
    const request: DeletePyramidalShareholderPyramidYearsRequest = {
      pyramidalShareholderId: this.pyramidalShareholderId,
      pyramidPyramidalShareholderId: pyramidItem.pyramidPyramidalShareholderId || undefined,
      largeShareholderId: pyramidItem.largeShareholderId || undefined,
      companyId: pyramidItem.companyId || undefined,
      opbCompanyId: pyramidItem.opbCompanyId || undefined,
      directorId: pyramidItem.directorId || undefined,
      years: selectedYears
    };

    this.pyramidalShareholderService.deletePyramidYears(request).subscribe({
      next: () => {
        this.spinnerService.hide();
        this.toast.success(`${itemName} deleted from ${selectedYears.length} ${selectedYears.length === 1 ? 'year' : 'years'}`, {
          iconTheme: {
            primary: 'var(--mat-sys-primary)',
            secondary: '#FFF',
          }
        });

        // Reload pyramid data
        this.loadPyramidalShareholderData();
      },
      error: (error) => {
        this.spinnerService.hide();
        this.toast.error(`<strong>Failed to delete pyramid item:</strong></br></br> ${Helpers.parseApiError(error)}`);
        console.error('Error deleting pyramid item:', error);
      }
    });
  }
}
